import { useMemo } from 'react';
import { getRadarCapabilities, getRadarRange, getRadarSource, isEntityBuried } from './radarUtils';

function getCoordinatePoint(entity, origin, width, range, type, extra = {}) {
  if (!entity || entity.coordinate === null || entity.coordinate === undefined) return null;
  const x = Number(entity.coordinate) % width;
  const y = Math.floor(Number(entity.coordinate) / width);
  const ox = Number(origin.coordinate) % width;
  const oy = Math.floor(Number(origin.coordinate) / width);

  const dx = x - ox;
  const dy = y - oy;
  const distance = Math.sqrt((dx * dx) + (dy * dy));
  if (distance > range || (dx === 0 && dy === 0)) return null;

  const normalized = distance / Math.max(1, range);
  const angle = Math.atan2(dy, dx);
  const radius = Math.min(42, 8 + normalized * 42);

  return {
    id: `${type}-${entity.id ?? entity.id_item ?? entity.coordinate}`,
    type,
    buried: Boolean(extra.buried),
    angle,
    radius,
    distance,
    x: 50 + Math.cos(angle) * radius,
    y: 50 + Math.sin(angle) * radius
  };
}

export default function RadarPanel({
  bot,
  items = [],
  enemies = [],
  cities = [],
  objects = [],
  missionTarget = null,
  width = 20,
  compact = false,
  open = true,
  onToggle = null,
  className = ''
}) {
  const radarSource = useMemo(() => getRadarSource(items), [items]);
  const radarPower = Number(radarSource?.power || 0);
  const radarRange = getRadarRange(radarPower);
  const radarCapabilities = useMemo(() => getRadarCapabilities(radarSource), [radarSource]);

  const points = useMemo(() => {
    if (!bot || radarRange <= 0) return [];
    const origin = { coordinate: Number(bot.coordinate || 0) };
    const scanObjects = objects.length > 0 ? objects : cities;
    const pointsAccumulator = [];
    const classifyEntity = (entry) => {
      if (Number(entry?.item_type || 0) === 7) return 'mine';
      if (isEntityBuried(entry)) return 'buried';
      return 'object';
    };

    if (radarCapabilities.surfaceDetection) {
      pointsAccumulator.push(
        ...scanObjects
          .filter((entry) => !isEntityBuried(entry) || Number(entry?.item_type || 0) === 7)
          .map((entry) => getCoordinatePoint(entry, origin, width, radarRange, classifyEntity(entry)))
          .filter(Boolean)
      );
    }

    if (radarCapabilities.buriedDetection) {
      pointsAccumulator.push(
        ...scanObjects
          .filter((entry) => isEntityBuried(entry))
          .map((entry) => getCoordinatePoint(entry, origin, width, radarRange, classifyEntity(entry), { buried: true }))
          .filter(Boolean)
      );
    }

    if (radarCapabilities.enemyDetection) {
      pointsAccumulator.push(
        ...enemies
          .map((entry) => getCoordinatePoint(entry, origin, width, radarRange, 'enemy'))
          .filter(Boolean)
      );
    }

    if (missionTarget && Number(missionTarget.coordinate) >= 0) {
      const missionPoint = getCoordinatePoint(
        {
          id: missionTarget.eventId || missionTarget.coordinate,
          coordinate: Number(missionTarget.coordinate)
        },
        origin,
        width,
        radarRange,
        'mission'
      );
      if (missionPoint) {
        pointsAccumulator.push(missionPoint);
      }
    }

    return pointsAccumulator;
  }, [bot, cities, enemies, missionTarget, objects, radarCapabilities.buriedDetection, radarCapabilities.enemyDetection, radarCapabilities.surfaceDetection, radarRange, width]);

  const shellPadding = compact ? 'p-2.5' : 'p-4';
  const radarSize = compact ? 'max-w-[198px]' : 'max-w-[330px]';
  const hasRadar = radarPower > 0;
  const closedBadgeClass = 'inline-flex w-fit items-center gap-2 rounded-full border border-cyan-500/20 bg-[#08101f]/95 px-2 py-1 shadow-[0_0_18px_rgba(0,255,255,0.06)]';

  return (
    <section className={`${open ? `rounded-2xl border border-cyan-500/30 bg-[#08101f] ${shellPadding}` : closedBadgeClass} ${className}`}>
      <div className={open ? 'flex items-center justify-end' : 'flex items-center gap-2'}>
        <button
          type="button"
          onClick={onToggle}
          className={`inline-flex items-center justify-center rounded-full border border-cyan-400/40 bg-black/65 text-[11px] leading-none text-cyan-100 backdrop-blur hover:bg-cyan-500/20 ${
            open ? 'h-6 w-6' : 'h-5 w-5 shrink-0'
          }`}
          title={open ? 'Cerrar radar' : 'Abrir radar'}
          aria-label={open ? 'Cerrar radar' : 'Abrir radar'}
        >
          {open ? '-' : '+'}
        </button>
        {!open && <span className="text-[10px] font-medium text-cyan-100/80">Radar</span>}
      </div>

      {open ? (
        hasRadar ? (
          <div className="mt-2 rounded-2xl border border-cyan-400/30 bg-[#04060d] p-2.5 shadow-[inset_0_0_60px_rgba(0,255,255,0.08)]">
            <div
              className={`relative mx-auto aspect-square overflow-hidden rounded-full border border-cyan-500/35 bg-[radial-gradient(circle_at_center,rgba(34,211,238,0.14)_0%,rgba(4,6,13,0.9)_55%,rgba(4,6,13,1)_100%)] ${radarSize}`}
            >
              <div className="absolute inset-0 rounded-full bg-[linear-gradient(to_right,transparent_49.5%,rgba(34,211,238,0.35)_50%,transparent_50.5%),linear-gradient(to_bottom,transparent_49.5%,rgba(34,211,238,0.35)_50%,transparent_50.5%),linear-gradient(45deg,transparent_49.5%,rgba(34,211,238,0.12)_50%,transparent_50.5%),linear-gradient(-45deg,transparent_49.5%,rgba(34,211,238,0.12)_50%,transparent_50.5%)] bg-[size:100%_100%,100%_100%,100%_100%,100%_100%]" />
              <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,transparent_0_34%,rgba(34,211,238,0.08)_35%,transparent_36%)]" />

              <div className="absolute inset-0 animate-pulse rounded-full border border-cyan-400/10" />
              <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-cyan-300/15" />
              <div className="absolute top-1/2 left-0 h-px w-full -translate-y-1/2 bg-cyan-300/15" />
              <div className="absolute left-0 top-0 h-full w-full rotate-45 border-t border-cyan-300/12" />
              <div className="absolute left-0 top-0 h-full w-full -rotate-45 border-t border-cyan-300/12" />

              <div className="absolute inset-[14%] rounded-full border border-cyan-400/10" />
              <div className="absolute inset-[28%] rounded-full border border-cyan-400/10" />
              <div className="absolute inset-[42%] rounded-full border border-cyan-400/10" />

              <div className="absolute inset-0">
                <div className="absolute left-1/2 top-1/2 h-1 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-yellow-300 shadow-[0_0_16px_rgba(250,204,21,0.9)]" />

                {points.map((point) => {
                  const pointTitle = point.type === 'enemy'
                    ? `Enemigo a ${point.distance.toFixed(1)} casillas`
                    : point.type === 'mission'
                      ? `Siguiente evento de mision a ${point.distance.toFixed(1)} casillas`
                      : point.type === 'mine'
                        ? `Mina detectada a ${point.distance.toFixed(1)} casillas`
                        : point.buried
                          ? `Objeto enterrado a ${point.distance.toFixed(1)} casillas`
                          : `Objeto a ${point.distance.toFixed(1)} casillas`;

                  if (point.type === 'mission') {
                    return (
                      <div
                        key={point.id}
                        className="absolute -translate-x-1/2 -translate-y-1/2"
                        style={{
                          left: `${point.x}%`,
                          top: `${point.y}%`
                        }}
                        title={pointTitle}
                      >
                        <div className="radar-mission-ping absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-yellow-200/70" />
                        <div className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-[3px] border border-yellow-100 bg-yellow-300 shadow-[0_0_14px_rgba(253,224,71,0.95)]" />
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[9px] font-black leading-none text-[#201100]">
                          !
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={point.id}
                      className={`absolute ${compact ? 'h-2 w-2' : 'h-2.5 w-2.5'} -translate-x-1/2 -translate-y-1/2 rounded-full shadow-[0_0_12px_currentColor] ${
                        point.type === 'enemy'
                          ? 'bg-rose-400 text-rose-400'
                          : point.type === 'mine'
                            ? 'bg-orange-400 text-orange-400 ring-1 ring-orange-300/40'
                            : point.buried
                              ? 'bg-amber-400 text-amber-400 ring-1 ring-amber-300/40'
                              : 'bg-emerald-400 text-emerald-400'
                      }`}
                      style={{
                        left: `${point.x}%`,
                        top: `${point.y}%`
                      }}
                      title={pointTitle}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-2 rounded-2xl border border-amber-400/30 bg-amber-500/10 px-3 py-4 text-amber-100 shadow-[inset_0_0_30px_rgba(251,191,36,0.08)]">
            <div className="text-sm font-black uppercase tracking-[0.18em] text-amber-200">
              ALERTA: no tienes radar
            </div>
            <p className="mt-2 text-lg font-semibold leading-tight text-amber-50">
              Compra un radar en la tienda y equipalo en tu bot para activar este panel.
            </p>
            <p className="mt-2 text-xs leading-relaxed text-amber-100/85">
              Cuando lo compres, vuelve a tu bot, equipalo y el panel mostrara la deteccion automatica de senales cercanas.
            </p>
          </div>
        )
      ) : (
        <span className="sr-only">Radar oculto</span>
      )}
    </section>
  );
}
